import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  listChannelBumpers,
  uploadChannelBumper,
  deleteChannelBumper,
  channelBumperPreviewUrl,
} from "@/lib/api";

/**
 * ChannelBumpersSection — upload + preview + delete the standard
 * INTRO and OUTRO MP4 clips attached to a saved channel kit.
 *
 * The stitched final video generated from any storyboard belonging to
 * this channel will automatically prepend the intro and append the
 * outro (with the same horizontal-blur xfade used between scenes).
 *
 * Constraints (enforced server-side): mp4 container, ≤50 MB, ≤30 s.
 *
 * Renders as a compact side-by-side pair of slots. Each slot shows:
 *  • an empty upload dropzone when the kind has no bumper yet,
 *  • an inline <video> preview + remove button when one is set,
 *  • a busy state while the upload/delete request is in flight.
 */
export default function ChannelBumpersSection({ jobId }) {
  const [bumpers, setBumpers] = useState({ intro: null, outro: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listChannelBumpers(jobId);
      setBumpers(res?.bumpers || { intro: null, outro: null });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't load bumpers";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <section className="mb-8" data-testid="ck-bumpers-section-error">
        <SectionHeader />
        <div className="border border-red-500/40 bg-red-500/5 p-4 rounded-sm text-xs text-red-200 font-mono">
          {error}
        </div>
      </section>
    );
  }

  return (
    <section className="mb-8" data-testid="ck-bumpers-section">
      <SectionHeader />
      <p className="text-xs text-zinc-400 mb-4 max-w-2xl leading-relaxed">
        Optional. If present, these clips are automatically stitched to the
        front (intro) and end (outro) of every final video assembled from a
        storyboard on this channel. MP4, up to 30 seconds, 50 MB max.
      </p>
      <div className="grid md:grid-cols-2 gap-6">
        <BumperSlot
          kind="intro"
          label="Intro clip"
          jobId={jobId}
          bumper={bumpers.intro}
          disabled={loading}
          onChanged={load}
        />
        <BumperSlot
          kind="outro"
          label="Outro clip"
          jobId={jobId}
          bumper={bumpers.outro}
          disabled={loading}
          onChanged={load}
        />
      </div>
    </section>
  );
}

function SectionHeader() {
  return (
    <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">
      04 · Intro & Outro
    </div>
  );
}

function BumperSlot({ kind, label, jobId, bumper, disabled, onChanged }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const pickFile = () => {
    if (busy || disabled) return;
    inputRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    // Reset immediately so the same file can be re-selected after error
    e.target.value = "";
    if (!file) return;
    if (!/\.mp4$|video\/mp4/i.test(file.name + " " + file.type)) {
      toast.error("Only .mp4 files are accepted");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File is over 50 MB");
      return;
    }
    setBusy(true);
    try {
      await uploadChannelBumper(jobId, kind, file);
      toast.success(`${label} uploaded`);
      onChanged?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || `Failed to upload ${label}`;
      toast.error(String(msg).slice(0, 200));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (busy) return;
    if (!window.confirm(`Remove ${label}?`)) return;
    setBusy(true);
    try {
      await deleteChannelBumper(jobId, kind);
      toast.success(`${label} removed`);
      onChanged?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || `Failed to remove ${label}`;
      toast.error(String(msg).slice(0, 200));
    } finally {
      setBusy(false);
    }
  };

  const testKind = kind;
  const previewUrl = bumper ? channelBumperPreviewUrl(bumper.preview_url) : "";

  return (
    <div
      data-testid={`ck-bumper-slot-${testKind}`}
      className="border border-zinc-800 bg-zinc-950/50 rounded-sm p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-yellow-400">
          {label}
        </div>
        {bumper && (
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            {bumper.duration_sec ? `${bumper.duration_sec.toFixed(1)}s · ` : ""}
            {(bumper.size_bytes / (1024 * 1024)).toFixed(1)} MB
          </div>
        )}
      </div>

      {bumper && previewUrl ? (
        <div>
          <video
            key={bumper.blob_id}
            src={previewUrl}
            controls
            preload="metadata"
            data-testid={`ck-bumper-video-${testKind}`}
            className="w-full aspect-video bg-black rounded-sm border border-zinc-900"
          />
          <div className="flex items-center gap-2 mt-3">
            <button
              type="button"
              onClick={pickFile}
              disabled={busy}
              data-testid={`ck-bumper-replace-${testKind}`}
              className="font-mono text-[11px] uppercase tracking-[0.25em] text-yellow-400 border border-yellow-400/60 hover:border-yellow-400 hover:bg-yellow-400/10 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-50"
            >
              {busy ? "…" : "↻ replace"}
            </button>
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              data-testid={`ck-bumper-remove-${testKind}`}
              className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400 hover:text-red-400 border border-zinc-800 hover:border-red-500/60 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-50"
            >
              ✕ remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={pickFile}
          disabled={busy || disabled}
          data-testid={`ck-bumper-upload-${testKind}`}
          className="w-full aspect-video border border-dashed border-zinc-800 hover:border-yellow-400/60 bg-zinc-950 rounded-sm flex flex-col items-center justify-center gap-2 p-6 transition-colors disabled:opacity-50"
        >
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400">
            {busy ? "uploading…" : `+ upload ${label.toLowerCase()}`}
          </span>
          <span className="text-[10px] text-zinc-600 font-mono">
            mp4 · ≤30s · ≤50MB
          </span>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="video/mp4"
        className="hidden"
        onChange={onFile}
        data-testid={`ck-bumper-input-${testKind}`}
      />
    </div>
  );
}
